import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Upload, X } from 'lucide-react';
import {
  formatUploadSize,
  getUploadSizeError,
  MAX_UPLOAD_SIZE_MB,
  uploadFile,
  type UploadProgress,
} from '@/components/business-ui/api/files/service';
import type {
  SpeechTopic,
  CreateSpeechTopicReq,
  UpdateSpeechTopicReq,
  ContentCategory,
  ContentDirection,
  SuitableGrade,
} from '@shared/api.interface';
import {
  CONTENT_CATEGORY_LABELS,
  CONTENT_DIRECTION_LABELS,
  SUITABLE_GRADE_LABELS,
} from '@shared/api.interface';
import { isRequiredResourceContent, isValidTopicDuration } from '@shared/topic-validation';

interface TopicFormDialogProps {
  topic: SpeechTopic | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateSpeechTopicReq | UpdateSpeechTopicReq) => Promise<void>;
  onDelete?: (topic: SpeechTopic) => void;
  downloadLink?: string;
}

export function TopicFormDialog({
  topic,
  open,
  onOpenChange,
  onSubmit,
  onDelete,
  downloadLink = '',
}: TopicFormDialogProps) {
  const isEdit = !!topic;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [uploadingType, setUploadingType] = useState<'cover' | 'video' | null>(null);

  const [formData, setFormData] = useState<{
    title?: string;
    suitable_grade?: SuitableGrade;
    content_category?: ContentCategory;
    content_direction?: ContentDirection;
    estimated_duration?: string;
    download_link?: string;
    cover_image?: string;
    video_display?: string;
  }>({
    title: '',
    suitable_grade: undefined,
    content_category: undefined,
    content_direction: undefined,
    estimated_duration: '',
    download_link: '',
    cover_image: undefined,
    video_display: undefined,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (topic) {
      setFormData({
        title: topic.title,
        suitable_grade: topic.suitable_grade,
        content_category: topic.content_category,
        content_direction: topic.content_direction,
        estimated_duration: topic.estimated_duration,
        download_link: downloadLink,
        cover_image: topic.cover_image || undefined,
      });
    } else {
      setFormData({
        title: '',
        suitable_grade: undefined,
        content_category: undefined,
        content_direction: undefined,
        estimated_duration: '',
        download_link: '',
        cover_image: undefined,
        video_display: undefined,
      });
    }
    setErrors({});
    setUploadProgress(null);
    setUploadingType(null);
  }, [topic, open, downloadLink]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title?.trim()) newErrors.title = '请输入主题名称';
    if (!formData.suitable_grade) newErrors.suitable_grade = '请选择适用年级';
    if (!formData.content_category) newErrors.content_category = '请选择内容分类';
    if (!formData.content_direction) newErrors.content_direction = '请选择内容方向';
    if (!isValidTopicDuration(formData.estimated_duration)) {
      newErrors.estimated_duration = '时长格式不正确，请使用“2分钟”“1分30秒”或“90秒”';
    }
    if (!isRequiredResourceContent(formData.download_link)) {
      newErrors.download_link = '请填写领取资源内容';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        title: formData.title?.trim() || '',
        suitable_grade: formData.suitable_grade as SuitableGrade,
        content_category: formData.content_category as ContentCategory,
        content_direction: formData.content_direction as ContentDirection,
        estimated_duration: formData.estimated_duration?.trim() || '',
        cover_image: formData.cover_image || null,
        video_display: formData.video_display || null,
        download_link: formData.download_link?.trim() || '',
        ...(isEdit ? {} : { content_description: '' }),
      } as CreateSpeechTopicReq | UpdateSpeechTopicReq;
      await onSubmit(payload);
      onOpenChange(false);
    } catch (error: any) {
      const message = error?.response?.data?.message || (isEdit ? '更新失败' : '创建失败');
      setErrors({ submit: Array.isArray(message) ? message[0] : message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileUpload = async (file: File, type: 'cover' | 'video') => {
    if (!file || isUploading) return;

    const sizeError = getUploadSizeError(file);
    if (sizeError) {
      setErrors((prev) => ({ ...prev, [type]: sizeError }));
      setUploadProgress(null);
      return;
    }

    setIsUploading(true);
    setUploadingType(type);
    setUploadProgress(null);
    try {
      const data = await uploadFile(file, undefined, (progress) => setUploadProgress(progress));
      const fileUrl = data.url;

      if (type === 'cover') {
        setFormData((prev) => ({ ...prev, cover_image: fileUrl }));
      } else {
        setFormData((prev) => ({ ...prev, video_display: fileUrl }));
      }

    } catch (error: any) {
      setErrors((prev) => ({ ...prev, [type]: error.message || '上传失败' }));
    } finally {
      setIsUploading(false);
      setUploadingType(null);
      setUploadProgress(null);
    }
  };

  const removeFile = (type: 'cover' | 'video') => {
    if (type === 'cover') {
      setFormData((prev) => ({ ...prev, cover_image: undefined }));
    } else {
      setFormData((prev) => ({ ...prev, video_display: undefined }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? '编辑演讲主题' : '新建演讲主题'}</DialogTitle>
          <DialogDescription>
            {isEdit ? '修改演讲主题信息' : '创建一个新的演讲主题'}。单个文件不超过 {MAX_UPLOAD_SIZE_MB}MB，上传中会显示阶段、进度和速度。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* 主题名称 */}
          <div className="space-y-2">
            <Label htmlFor="title">
              主题名称 <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="请输入主题名称"
              className={errors.title ? 'border-destructive' : ''}
            />
            {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
          </div>

          {/* 适用年级和内容分类 */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                适用年级 <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.suitable_grade}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, suitable_grade: value as SuitableGrade }))
                }
              >
                <SelectTrigger className={errors.suitable_grade ? 'border-destructive' : ''}>
                  <SelectValue placeholder="选择年级" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{SUITABLE_GRADE_LABELS.low}</SelectItem>
                  <SelectItem value="high">{SUITABLE_GRADE_LABELS.high}</SelectItem>
                </SelectContent>
              </Select>
              {errors.suitable_grade && (
                <p className="text-sm text-destructive">{errors.suitable_grade}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>
                内容分类 <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.content_category}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, content_category: value as ContentCategory }))
                }
              >
                <SelectTrigger className={errors.content_category ? 'border-destructive' : ''}>
                  <SelectValue placeholder="选择分类" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CONTENT_CATEGORY_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.content_category && (
                <p className="text-sm text-destructive">{errors.content_category}</p>
              )}
            </div>
          </div>

          {/* 内容方向 */}
          <div className="space-y-2">
            <Label>
              内容方向 <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.content_direction}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, content_direction: value as ContentDirection }))
              }
            >
              <SelectTrigger className={errors.content_direction ? 'border-destructive' : ''}>
                <SelectValue placeholder="请选择方向" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CONTENT_DIRECTION_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.content_direction && (
              <p className="text-sm text-destructive">{errors.content_direction}</p>
            )}
          </div>

          {/* 预计时长 */}
          <div className="space-y-2">
            <Label htmlFor="duration">
              预计时长 <span className="text-destructive">*</span>
            </Label>
            <Input
              id="duration"
              value={formData.estimated_duration}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, estimated_duration: e.target.value }))
              }
              placeholder="例如：5分钟"
              className={errors.estimated_duration ? 'border-destructive' : ''}
            />
            {errors.estimated_duration && (
              <p className="text-sm text-destructive">{errors.estimated_duration}</p>
            )}
          </div>

          {/* 封面图上传 */}
          <div className="space-y-2">
            <Label>封面图</Label>
            {formData.cover_image ? (
              <div className="relative w-40 h-28 rounded-lg overflow-hidden border">
                <img
                  src={formData.cover_image}
                  alt="封面"
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => removeFile('cover')}
                  className="absolute top-1 right-1 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.currentTarget.value = '';
                    if (file) void handleFileUpload(file, 'cover');
                  }}
                  className="hidden"
                  id="cover-upload"
                  disabled={isUploading}
                />
                <Label
                  htmlFor="cover-upload"
                  className="flex items-center gap-2 px-4 py-2 border rounded-md cursor-pointer hover:bg-accent"
                >
                  <Upload className="h-4 w-4" />
                  {isUploading ? '上传中...' : '上传封面'}
                </Label>
              </div>
            )}
            {uploadProgress && uploadingType === 'cover' && <LegacyUploadProgressView progress={uploadProgress} />}
            {errors.cover && <p className="text-sm text-destructive">{errors.cover}</p>}
          </div>

          {/* 领取资源内容 */}
          <div className="space-y-2">
            <Label htmlFor="download-link">
              领取资源内容 <span className="text-destructive">*</span>
            </Label>
            <Input
              id="download-link"
              value={formData.download_link}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, download_link: e.target.value }))
              }
              placeholder="填写领取资源内容（链接、提取码或说明均可）"
              className={errors.download_link ? 'border-destructive' : ''}
            />
            {errors.download_link && (
              <p className="text-sm text-destructive">{errors.download_link}</p>
            )}
          </div>

          {/* 视频上传 */}
          <div className="space-y-2">
            <Label>视频展示</Label>
            {formData.video_display ? (
              <div className="relative w-full max-w-md">
                <video
                  src={formData.video_display}
                  controls
                  className="w-full rounded-lg"
                />
                <button
                  onClick={() => removeFile('video')}
                  className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="video/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.currentTarget.value = '';
                    if (file) void handleFileUpload(file, 'video');
                  }}
                  className="hidden"
                  id="video-upload"
                  disabled={isUploading}
                />
                <Label
                  htmlFor="video-upload"
                  className="flex items-center gap-2 px-4 py-2 border rounded-md cursor-pointer hover:bg-accent"
                >
                  <Upload className="h-4 w-4" />
                  {isUploading ? '上传中...' : '上传视频'}
                </Label>
              </div>
            )}
            <p className="text-xs text-slate-500">单个文件不超过 {MAX_UPLOAD_SIZE_MB}MB</p>
            {uploadProgress && uploadingType === 'video' && <LegacyUploadProgressView progress={uploadProgress} />}
            {errors.video && <p className="text-sm text-destructive">{errors.video}</p>}
          </div>
        </div>

        {errors.submit && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
            {errors.submit}
          </div>
        )}

        {isEdit && topic && onDelete && (
          <div className="flex items-center justify-between gap-4 rounded-md border border-red-200 bg-red-50 px-3 py-3">
            <div>
              <div className="text-sm font-medium text-red-800">危险操作</div>
              <p className="mt-1 text-xs text-red-700">永久删除“{topic.title}”，历史领取记录会保留。</p>
            </div>
            <Button
              type="button"
              variant="outline"
              className="shrink-0 border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
              disabled={isSubmitting || isUploading}
              onClick={() => onDelete(topic)}
            >
              永久删除
            </Button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting || isUploading}>
            取消
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || isUploading}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEdit ? '保存' : '创建'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LegacyUploadProgressView({ progress }: { progress: UploadProgress }) {
  const phaseLabel = {
    preparing: '准备上传',
    uploading: '正在上传到应用服务器',
    storing: '正在写入 COS，等待服务器确认',
  }[progress.phase];
  const elapsedSeconds = Math.max(0, Math.floor(progress.elapsedMs / 1000));
  const elapsed = elapsedSeconds < 60
    ? `${elapsedSeconds}秒`
    : `${Math.floor(elapsedSeconds / 60)}分${elapsedSeconds % 60}秒`;
  const speed = progress.bytesPerSecond && progress.bytesPerSecond > 0
    ? `${formatUploadSize(progress.bytesPerSecond)}/秒`
    : '计算中';
  const remaining = progress.percent !== null && progress.bytesPerSecond && progress.bytesPerSecond > 0
    ? Math.max(0, Math.ceil((progress.total - progress.loaded) / progress.bytesPerSecond))
    : null;
  const remainingText = remaining === null
    ? null
    : `预计剩 ${remaining < 60 ? `${remaining}秒` : `${Math.floor(remaining / 60)}分${remaining % 60}秒`}`;

  return (
    <div className="rounded-md border border-orange-200 bg-orange-50/60 p-2" role="status" aria-live="polite">
      <div className="flex items-center justify-between gap-2 text-xs font-medium text-orange-800">
        <span>{phaseLabel}</span>
        <span>{progress.percent === null ? '处理中' : `${progress.percent}%`}</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-orange-100">
        <div
          className={progress.percent === null ? 'h-full w-1/3 animate-pulse rounded-full bg-orange-500' : 'h-full rounded-full bg-orange-500 transition-[width]'}
          style={progress.percent === null ? undefined : { width: `${progress.percent}%` }}
        />
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-orange-700">
        <span>{formatUploadSize(progress.loaded)} / {formatUploadSize(progress.total)}</span>
        <span>{speed}</span>
        <span>已用 {elapsed}</span>
        {remainingText && <span>{remainingText}</span>}
      </div>
    </div>
  );
}
